import React, {useEffect, useRef, useMemo, useReducer, useContext} from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';

import {classNames} from '~/src/utilities/helpers/css';
import {SlideshowFormat, ImageFormat} from '~/src/utilities/constants/formats';
import {base64Encode} from '~/src/utilities/helpers/string';
import {SettingsContext} from '~/src/components/context/SettingsContext';
import {SiteArea} from '~/src/utilities/constants/navigation';

import css from './Image.module.scss';

const getSortedKeyArray = (obj) =>
    Object.keys(obj)
        .map((variant) => obj[variant])
        .reduce((variants, variant, index, all) => {
            if (!variants.some((c) => c.maxWidth === variant.maxWidth && c.maxHeight === variant.maxHeight)) {
                variants.push(variant);
            }

            return variants;
        }, [])
        .sort((a, b) => {
            if (a.width > b.width) {
                return 1;
            }

            if (a.width < b.width) {
                return -1;
            }

            return a.src.localeCompare(b.src);
        });

const imagePosition = ({focusPoint, aspectRatio, width, height}, node = null) => {
    if (!focusPoint) {
        return '50% 50%';
    }

    if (node) {
        const nodeAspect = node.offsetWidth / node.offsetHeight;
        const imageAspect = aspectRatio || width / height;

        if (nodeAspect > imageAspect) {
            const nodeHeight = node.offsetHeight;
            const imageHeight = node.offsetWidth / imageAspect;
            const overflowHeight = imageHeight - nodeHeight;
            const overflowY = imageHeight * focusPoint.y - nodeHeight / 2;

            const yPos = Math.min(Math.max(overflowY / overflowHeight, 0.0), 1.0);

            return `50% ${Math.round(yPos * 100)}%`;
        }

        const nodeWidth = node.offsetWidth;
        const imageWidth = node.offsetHeight * imageAspect;
        const overflowWidth = imageWidth - nodeWidth;
        const overflowX = overflowWidth * focusPoint.x - nodeWidth / 2;

        const xPos = Math.min(Math.max(overflowX / overflowWidth, 0.0), 1.0);
        return `50% ${Math.round(xPos * 100)}%`;
    }

    return `${Math.round(focusPoint.x * 100)}% ${Math.round(focusPoint.y * 100)}%`;
};

const ActionTypes = {
    LOAD: 'LOAD',
    RESIZE: 'RESIZE'
};

const reducer = (state, action) => {
    switch (action.type) {
        case ActionTypes.RESIZE:
            return {
                ...state,
                position: imagePosition(action.variant, action.node)
            };
        case ActionTypes.LOAD:
            return {
                ...state,
                isLoaded: true,
                position: imagePosition(action.variant, action.node)
            };
        default:
            return state;
    }
};

const getContainerStyles = ({format, variants, promcolor, maxAspectRatio, siteArea}) => {
    const styles = {};

    switch (format) {
        case SlideshowFormat.SLIDESHOW_INLINE:
            const slideshowAspectRatio = parseFloat(css.slideshow);
            styles.paddingBottom = `${100 / slideshowAspectRatio}%`;
            if (siteArea !== SiteArea.LIFESTYLE) {
                styles.backgroundColor = css.imgBackgroundColor;
            }
            break;
        case ImageFormat.IMAGE_COVER:
            styles.backgroundColor = promcolor;
            break;
        case SlideshowFormat.SLIDESHOW_FULLSCREEN:
            break;
        default:
            const aspectRatio = get(variants, '[0].aspectRatio') || get(variants, '[0].width', 0) / get(variants, '[0].height', 1);
            const maxHeightAspectRatio = Math.max(aspectRatio, maxAspectRatio);
            styles.paddingBottom = `${100 / maxHeightAspectRatio}%`;
            break;
    }

    return styles;
};

const getImageStyles = ({format, variants, position}) => {
    const styles = {};

    if (format === ImageFormat.IMAGE_COVER) {
        styles.objectPosition = position;
    }

    return styles;
};

const getBackgroundImage = ({src}) => {
    const styles = {};

    if (src) {
        styles.backgroundImage = `url(${src})`;
    }
    return styles;
};

const getFallbackStyles = ({format, promcolor, variants, position, siteArea}) => {
    if (!process.browser || 'objectFit' in document.documentElement.style === true) {
        return {};
    }

    const variant = variants.find(({maxWidth}) => maxWidth >= 800) || variants[0];

    const styles = {
        backgroundImage: `url('${variant.src}')`
    };

    switch (format) {
        case SlideshowFormat.SLIDESHOW_FULLSCREEN:
        case SlideshowFormat.SLIDESHOW_INLINE:
            if (siteArea !== SiteArea.LIFESTYLE) {
                styles.backgroundColor = css.imgBackgroundColor;
            }
            break;
        case ImageFormat.IMAGE_COVER:
            styles.backgroundColor = promcolor;
            styles.backgroundPosition = position;
            break;
        default:
            break;
    }

    return styles;
};

const Image = ({
    format,
    promcolor,
    name,
    caption,
    captionTitle,
    sizes,
    variants,
    maxAspectRatio = 0,
    lazyLoading = true,
    hideTitle = false,
    hasBackgroundImage = false
}) => {
    const sortedVariants = useMemo(() => getSortedKeyArray(variants), [variants]);

    if (!sortedVariants || !sortedVariants.length) {
        return null;
    }

    const {siteArea} = useContext(SettingsContext);

    const src = sortedVariants[0].src;
    const srcSet = useMemo(() => sortedVariants.map((variant) => `${variant.src} ${variant.width}w`), [sortedVariants]);

    const containerRef = useRef();
    const [state, dispatch] = useReducer(reducer, {
        isLoaded: false,
        position: imagePosition(sortedVariants[0])
    });

    const containerStyles = useMemo(() => getContainerStyles({format, variants: sortedVariants, promcolor, maxAspectRatio, siteArea}), [
        format,
        sortedVariants,
        promcolor,
        maxAspectRatio
    ]);

    const imageStyles = useMemo(() => getImageStyles({format, variants: sortedVariants, position: state.position}), [format, sortedVariants, state.position]);

    const backgroundImageStyles = useMemo(() => hasBackgroundImage && getBackgroundImage({src}), [src, hasBackgroundImage]);

    const fallbackStyles = useMemo(() => getFallbackStyles({format, promcolor, variants: sortedVariants, position: state.position, siteArea}), [
        format,
        promcolor,
        sortedVariants,
        state.position
    ]);

    if (process.browser) {
        useEffect(() => {
            if (lazyLoading && 'IntersectionObserver' in window) {
                if (!state.isLoaded && containerRef.current) {
                    const options = {
                        rootMargin: '500px 500px 500px 500px',
                        threshold: 0.0
                    };

                    const observer = new IntersectionObserver((entries) => {
                        if (entries.length && entries[0].isIntersecting) {
                            dispatch({
                                type: ActionTypes.LOAD,
                                variant: sortedVariants[0],
                                node: containerRef.current
                            });
                        }
                    }, options);

                    observer.observe(containerRef.current);

                    return () => observer.disconnect();
                }
            } else {
                dispatch({
                    type: ActionTypes.LOAD,
                    variant: sortedVariants[0],
                    node: containerRef.current
                });
            }
        }, [state.isLoaded]);

        useEffect(() => {
            const onResize = () => {
                dispatch({
                    type: ActionTypes.RESIZE,
                    variant: sortedVariants[0],
                    node: containerRef.current
                });
            };

            window.addEventListener('resize', onResize);

            return () => window.removeEventListener('resize', onResize);
        }, []);
    }

    const placeholder = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><path fill="${promcolor || 'none'}" d="M0 0h1v1H0z"/></svg>`;
    const placeholderSrc = `data:image/svg+xml;base64,${base64Encode(placeholder)}`;

    const classList = classNames(css, 'imgContainer', format);
    const title = name || captionTitle || (caption && caption.text);

    return (
        <div className={classList} style={containerStyles} ref={containerRef}>
            {hasBackgroundImage && <div className={css.backgroundImage} style={backgroundImageStyles}></div>}
            <img
                src={state.isLoaded ? src : placeholderSrc}
                alt={title}
                title={hideTitle ? '' : title}
                srcSet={state.isLoaded ? srcSet : ''}
                sizes={sizes}
                style={imageStyles}
                width={get(variants, '[0].width', 0)}
                height={get(variants, '[0].height', 0)}
            />
            {state.isLoaded && <div className={css.fallback} style={fallbackStyles}></div>}
        </div>
    );
};

if (process.env.NODE_ENV === 'development') {
    Image.propTypes = {
        name: PropTypes.string,
        caption: PropTypes.shape(),
        captionTitle: PropTypes.string,
        promcolor: PropTypes.string,
        format: PropTypes.string,
        maxAspectRatio: PropTypes.number,
        variants: PropTypes.objectOf(
            PropTypes.shape({
                src: PropTypes.string.isRequired,
                width: PropTypes.number,
                height: PropTypes.number,
                aspectRatio: PropTypes.number,
                focusPoint: PropTypes.shape({
                    x: PropTypes.number.isRequired,
                    y: PropTypes.number.isRequired
                })
            })
        ).isRequired,
        sizes: PropTypes.string,
        lazyLoading: PropTypes.bool,
        hideTitle: PropTypes.bool,
        hasBackgroundImage: PropTypes.bool
    };
}

export default Image;
